import {
  ImageBackground,
  StyleSheet,
  TouchableOpacity,
  View,
  Image,
  TextInput,
  Keyboard,
  ActivityIndicator,
  Alert,
  Platform,
} from "react-native";
import React, { useState, useRef, useEffect } from "react";
import { AppIcons } from "../../assets/images";
import FastImage from "react-native-fast-image";
import { Text } from "react-native-gesture-handler";
import { userAction } from "../../redux/reducers/user";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { AppTheme } from "../../utils/theme";
import LinearGradient from "react-native-linear-gradient";
import { selectImage } from "../../utils/ImagePicker";
import api from "../../utils/axiosinstance";
import axios from "axios";
import { GET_USER_DETAIL, UPDATE_USER_PROFILE, UPLOAD_PHOTO } from "../../utils/contants";
import { useAppSelector } from "../../redux/storeHook";
import Spacer from "../../components/global/Spacer";
import { showToastNotification } from "../../components/global/Toast";
import { ROUTE_NAME } from "../../utils/routes";

const EditProfile = () => {
  const { userData } = useAppSelector((s) => s.user);

  const [username, setUsername] = useState(userData?.name || "");
  const [userImage, setUserImage] = useState<string | null | any>(
    userData?.profilePicture || null
  );
  const [editMode, setEditMode] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [fetchingData, setFetchingData] = useState<boolean>(
    Boolean(userData?.access_token && !userData?.name)
  );
  const navigation = useNavigation<NativeStackNavigationProp<any>>();
  const usernameRef = useRef<TextInput>(null);
  const randomIndex = Math.floor(Math.random() * 1000);

  useEffect(() => {
    if (userData?.name) {
      setUsername(userData.name);
    }
    if (userData?.profilePicture) {
      setUserImage(userData.profilePicture);
    }
  }, [userData?.name, userData?.profilePicture]);

  useEffect(() => {
    const loadProfileIfNeeded = async () => {
      if (userData?.access_token && !userData?.name) {
        setFetchingData(true);
        try {
          const res = await api.get(GET_USER_DETAIL);
          if (res.data?.status && res.data?.user) {
            if (res.data.user.name) setUsername(res.data.user.name);
            if (res.data.user.profilePicture) setUserImage(res.data.user.profilePicture);
            userAction.updateUserField({
              key: "name",
              value: res.data.user.name,
            });
            userAction.updateUserField({
              key: "profilePicture",
              value: res.data.user.profilePicture,
            });
            userAction.updateUserField({
              key: "coins",
              value: res.data.user.coins,
            });
            userAction.updateUserField({
              key: "matchStats",
              value: res.data.user.matchStats,
            });
            userAction.updateUserField({
              key: "level",
              value: res.data.user.level,
            });
          }
        } catch (e) {
          console.log("Error loading profile details:", e);
        } finally {
          setFetchingData(false);
        }
      }
    };
    loadProfileIfNeeded();
  }, [userData?.access_token]);

  useEffect(() => {
    if (editMode) {
      const timer = setTimeout(() => {
        usernameRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      usernameRef.current?.blur();
    }
  }, [editMode]);

  const handleGoBack = () => {
    userAction.setTouchSound(true);
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate(ROUTE_NAME.HomeScreen);
    }
  };

  const toggleEdit = () => {
    setEditMode((prev) => !prev);
  };

  const openGallery = async () => {
    try {
      const image: any = await selectImage({});
      if (image) {
        setTimeout(() => {
          setUserImage(image?.path);
        }, 500);
      }
    } catch (err) {
      console.log("@ err select user image", err);
    }
  };

  const uploadedImages = async (image: any, index?: number) => {
    try {
      if (username == "") {
        Alert.alert(
          "Required field",
          "User name is required.",
          [
            {
              text: "OK",
              onPress: () => {
                console.log("Profile update cancelled");
              },
            },
          ],
          { cancelable: false }
        );
        return;
      }
      setLoading(true);

      let profilePictureUrl = userData?.profilePicture;

      if (image && image !== userData?.profilePicture) {
        const validIndex =
          typeof index === "number" ? index : Math.floor(Math.random() * 1000);
        const formData = new FormData();

        if (
          Platform.OS === "web" &&
          typeof image === "string" &&
          (image.startsWith("blob:") || image.startsWith("data:") || image.startsWith("http"))
        ) {
          try {
            const blobRes = await fetch(image);
            const blob = await blobRes.blob();
            (formData as any).append("profilePicture", blob, `image${validIndex}.jpg`);
          } catch (e) {
            console.log("Error fetching blob on web:", e);
            formData.append("profilePicture", {
              uri: image,
              type: "image/jpeg",
              name: `image${validIndex}.jpg`,
            } as any);
          }
        } else {
          formData.append("profilePicture", {
            uri: image,
            type: "image/jpeg",
            name: `image${validIndex}.jpg`,
          } as any);
        }

        const uploadRes = await api.patch(
          UPLOAD_PHOTO,
          formData,
          Platform.OS === "web"
            ? undefined
            : {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
        );

        if (uploadRes?.data?.status) {
          profilePictureUrl = uploadRes.data.profilePicture;
        } else {
          showToastNotification("", "Failed to upload image");
          setLoading(false);
          return;
        }
      }

      const res = await api.patch(UPDATE_USER_PROFILE, {
        name: username,
        profilePicture: profilePictureUrl,
      });

      if (res?.data?.status) {
        showToastNotification("", res.data?.message);
        userAction.updateUserField({
          key: "name",
          value: res?.data?.user?.name,
        });
        userAction.updateUserField({
          key: "profilePicture",
          value: res?.data?.user?.profilePicture,
        });
      }
    } catch (err: any) {
      setLoading(false);
      if (axios.isAxiosError(err) && err.response) {
        showToastNotification(
          "",
          err.response.data.message || "An error occurred"
        );
      } else {
        console.log("Unexpected error:", err);
        showToastNotification("", "An unexpected error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ImageBackground source={AppIcons.screenBackground} style={styles.mainView}>
      <View style={styles.titleView}>
        <Text style={styles.titleText}>{"Profile"}</Text>
        <View style={styles.profileView}>
          <FastImage
            source={
              userImage != null ? { uri: userImage } : AppIcons.DefProfile
            }
            style={userImage != null ? styles.defImage2 : styles.defImage}
          ></FastImage>
          <TouchableOpacity onPress={openGallery} style={styles.cameraView}>
            <Image
              source={AppIcons.Camera}
              resizeMode={"contain"}
              style={styles.proImage}
            />
          </TouchableOpacity>
        </View>
        <View style={styles.usernameContainer}>
          <TextInput
            ref={usernameRef}
            style={styles.usernameInput}
            value={username}
            onChangeText={setUsername}
            maxLength={15}
            onSubmitEditing={toggleEdit}
            placeholder="Enter user name"
            placeholderTextColor={AppTheme.colors.white}
            autoCapitalize="none"
            underlineColorAndroid="transparent"
            editable={editMode}
            showSoftInputOnFocus={true}
            selectTextOnFocus={true}
            returnKeyType="done"
          />
          <TouchableOpacity style={styles.editView} onPress={toggleEdit}>
            <Image source={AppIcons.EditIcon} style={styles.editImage} />
          </TouchableOpacity>
        </View>
        <Spacer size={20} />
        <View style={styles.statsContainer}>
          <View style={styles.statsHeaderView}>
            <Text style={styles.statsHeaderText}>Total games won</Text>
          </View>
          <View style={styles.rowView}>
            <Text style={styles.t1}>Games won</Text>
            <Text style={styles.t1}>
              {userData?.matchStats?.wins}/{userData?.matchStats?.totalGames}
            </Text>
          </View>
          <View style={styles.statsHeaderView}>
            <Text style={styles.statsHeaderText}>Level</Text>
          </View>
          <View style={styles.rowView}>
            <Text style={styles.t1}>Level</Text>
            <Text style={styles.t1}>{userData?.level}/250</Text>
          </View>
        </View>
        <View style={{ flex: 1 }} />
        <TouchableOpacity
          onPress={() => uploadedImages(userImage, randomIndex)}
          activeOpacity={1}
          disabled={loading}
          style={styles.appleWrapper}
        >
          <LinearGradient
            colors={[
              AppTheme.colors.linearGradient_5,
              AppTheme.colors.linearGradient_6,
            ]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.googleBtn1}
          >
            <LinearGradient
              colors={[
                AppTheme.colors.linearGradient_1,
                AppTheme.colors.linearGradient_2,
              ]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.googleBtn}
            >
              {loading ? (
                <ActivityIndicator
                  size={"small"}
                  color={AppTheme.colors.black}
                />
              ) : (
                <Text style={styles.googleText}>SAVE</Text>
              )}
            </LinearGradient>
          </LinearGradient>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.backButton}
          onPress={handleGoBack}
          activeOpacity={0.7}
        >
          <Image
            source={AppIcons.BackIcon}
            style={styles.backIcon}
            resizeMode="contain"
          />
        </TouchableOpacity>

        {fetchingData && (
          <View style={styles.loadingOverlay}>
            <ActivityIndicator size="large" color="#FFD700" />
            <Text style={styles.loadingOverlayText}>Loading profile data...</Text>
          </View>
        )}
      </View>
    </ImageBackground>
  );
};

const styles = StyleSheet.create({
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(11, 12, 27, 0.85)",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999,
  },
  loadingOverlayText: {
    marginTop: 12,
    color: AppTheme.colors.white,
    fontFamily: AppTheme.fonts.ALEXANDRIA_SEMIBOLD,
    fontSize: 15,
  },
  mainView: {
    flex: 1,
  },
  titleView: {
    flex: 1,
  },
  titleText: {
    textAlign: "center",
    color: AppTheme.colors.white,
    fontFamily: AppTheme.fonts.BUBBLE_BOBBLE,
    fontSize: 36,
    textShadowColor: AppTheme.colors.outline,
    textShadowOffset: { width: 2, height: 2 },
    textShadowRadius: 2,
    marginTop: "18%",
  },
  profileView: {
    marginTop: 40,
    alignItems: "center",
    alignSelf: "center",
    position: "relative",
    height: 180,
    overflow: "visible",
    width: 180,
  },
  cameraView: {
    position: "absolute",
    bottom: -10,
    right: -10,
    zIndex: 10,
  },
  defImage: {
    height: 180,
    width: 180,
    borderRadius: 20,
  },
  defImage2: {
    height: 180,
    width: 180,
    borderRadius: 20,
    borderWidth: 1.5,
    borderColor: AppTheme.colors.linearGradient_5,
  },

  defImage1: {
    height: 180,
    width: 180,
    borderRadius: 20,
  },
  proImage: {
    height: 35,
    width: 35,
  },
  backButton: {
    position: "absolute",
    top: Platform.OS === "web" ? 20 : 70,
    right: 0,
    width: 50,
    height: 40,
    zIndex: 99999,
    elevation: 999,
    cursor: "pointer",
  },
  backIcon: {
    width: 50,
    height: 40,
  },
  usernameContainer: {
    marginTop: 40,
    borderWidth: 2,
    borderRadius: 10,
    borderColor: AppTheme.colors.linearGradient_7,
    backgroundColor: "rgba(0,0,0,0.2)",
    flexDirection: "row",
    alignItems: "center",
    marginHorizontal: 30,
    position: "relative",
    justifyContent: "center",
  },
  usernameInput: {
    fontSize: 18,
    width: "70%",
    textAlign: "center",
    height: 55,
    color: AppTheme.colors.white,
    fontFamily: AppTheme.fonts.ALEXANDRIA_BOLD,
  },
  editView: {
    position: "absolute",
    right: 20,
    zIndex: 10,
  },
  editImage: {
    height: 20,
    width: 20,
  },
  appleWrapper: {
    alignSelf: "center",
    marginBottom: 30,
  },
  googleBtn: {
    width: 200,
    height: 50,
    borderRadius: 25,
    justifyContent: "center",
  },
  googleText: {
    fontFamily: AppTheme.fonts.BUBBLE_BOBBLE,
    fontSize: 35,
    color: AppTheme.colors.white,
    textAlign: "center",
    textShadowColor: AppTheme.colors.outline,
    textShadowOffset: { width: 2, height: 2 },
    textShadowRadius: 2,
  },
  googleBtn1: {
    width: 200,
    height: 55,
    borderRadius: 25,
  },
  statsContainer: {
    width: "85%",
    borderRadius: 10,
    backgroundColor: "rgba(0,0,0,0.1)",
    alignSelf: "center",
  },
  statsHeaderView: {
    width: "100%",
    borderWidth: 1,
    borderRadius: 10,
    borderColor: AppTheme.colors.linearGradient_7,
    backgroundColor: "rgba(0,0,0,0.2)",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    height: 42,
  },
  statsHeaderText: {
    color: AppTheme.colors.white,
    fontFamily: AppTheme.fonts.BUBBLE_BOBBLE,
    fontSize: 20,
    textShadowColor: AppTheme.colors.outline,
    textShadowOffset: { width: 2, height: 2 },
    textShadowRadius: 2,
  },
  rowView: {
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
    padding: 14,
  },
  t1: {
    fontSize: 16,
    color: AppTheme.colors.white,
    fontFamily: AppTheme.fonts.ALEXANDRIA_MEDIUM,
  },
});

export default EditProfile;
